import { useEffect, useLayoutEffect, useRef } from "react";
import { restoreDashboardContentScroll } from "../utils/exploreCoursesScrollRestore";

const RESTORE_MAX_ATTEMPTS = 12;

/**
 * Restores vertical scroll (and optional card anchor) when returning to a dashboard page.
 */
const useDashboardScrollRestore = ({
  isReady,
  restoreTrigger,
  readScrollState,
  clearScrollState,
}) => {
  const pendingRestoreRef = useRef(null);
  const hasRestoredRef = useRef(false);

  useLayoutEffect(() => {
    pendingRestoreRef.current = readScrollState();
  }, [readScrollState]);

  useEffect(() => {
    if (!isReady || hasRestoredRef.current || !pendingRestoreRef.current) return;

    const saved = pendingRestoreRef.current;
    let attempts = 0;
    let cancelled = false;

    const tryRestore = () => {
      if (cancelled || hasRestoredRef.current) return;

      attempts += 1;
      const restored = restoreDashboardContentScroll(saved);

      if (restored || attempts >= RESTORE_MAX_ATTEMPTS) {
        hasRestoredRef.current = true;
        pendingRestoreRef.current = null;
        clearScrollState();
        return;
      }

      requestAnimationFrame(tryRestore);
    };

    const timeoutId = window.setTimeout(() => {
      requestAnimationFrame(tryRestore);
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isReady, restoreTrigger, clearScrollState]);
};

export default useDashboardScrollRestore;
